import { cn } from '@forest/shared'
import { For, Show } from 'solid-js'
import { milestoneRewardImages } from '~/assets/rewards'
import type { MilestoneView } from '~/lib/milestones'
import { rewardVisual } from '~/lib/reward-visuals'
import { m } from '~/paraglide/messages'
import { Sprout } from './icons/sprout'
import { Spinner } from './spinner'

const ROW_HEIGHT = 80
const TOP_PAD = 40 // sapling zone above the first dot
const RAIL_TOP = 11 // rail and fill both start here, under the leaves
const HEAD0 = 21 // head Y at 0: the glow dot pokes out at the leaves' bottom
const CAP = 3 // the fill's rounded end-cap radius, centred on the head

/** Vertical centre (px) of milestone dot `i` within the timeline. */
function dotY(i: number): number {
  return TOP_PAD + ROW_HEIGHT * i + ROW_HEIGHT / 2
}

/**
 * Vertical position (px) of the current-progress head (the glowing dot). At
 * score 0 it sits at the sprout leaves' base (HEAD0), so the sprout reads as
 * the starting point. Past the first threshold it is piecewise-linearly
 * interpolated through each evenly-spaced dot (dots are evenly spaced,
 * thresholds are not).
 */
function markerY(score: number, thresholds: number[]): number {
  const n = thresholds.length
  if (n === 0 || score <= 0) return HEAD0
  if (score >= thresholds[n - 1]) return dotY(n - 1)
  if (score < thresholds[0]) {
    return HEAD0 + (score / thresholds[0]) * (dotY(0) - HEAD0)
  }
  for (let i = 0; i < n - 1; i++) {
    if (score < thresholds[i + 1]) {
      const f = (score - thresholds[i]) / (thresholds[i + 1] - thresholds[i])
      return dotY(i) + f * (dotY(i + 1) - dotY(i))
    }
  }
  return dotY(n - 1)
}

function StepCheck() {
  return (
    <svg width="9" height="7" viewBox="0 0 9 7" fill="none" aria-hidden="true">
      <path
        d="M1 3.5L3.33 5.83L8 1.17"
        stroke="white"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  )
}

function ClaimedTick(props: { class?: string }) {
  return (
    <svg
      width="31"
      height="26"
      viewBox="0 0 31 26"
      fill="none"
      aria-hidden="true"
      class={props.class}
    >
      <g transform="translate(-812.2 -94)">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M839.571 96.3477C842.408 98.6424 842.819 102.768 840.49 105.563L830.569 117.468C829.336 118.947 827.507 119.821 825.564 119.859C823.621 119.897 821.759 119.095 820.467 117.664L814.68 111.254C812.241 108.552 812.487 104.414 815.229 102.01C817.971 99.607 822.172 99.8491 824.612 102.551L825.231 103.237L830.218 97.2532C832.547 94.4585 836.735 94.053 839.571 96.3477Z"
          fill="white"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M837.271 99.1067C838.56 100.15 838.747 102.025 837.689 103.295L827.768 115.2C827.207 115.873 826.376 116.27 825.493 116.287C824.61 116.304 823.763 115.94 823.176 115.29L817.389 108.879C816.28 107.651 816.392 105.77 817.638 104.678C818.885 103.585 820.794 103.695 821.903 104.923L825.341 108.732L833.019 99.5183C834.078 98.248 835.982 98.0637 837.271 99.1067Z"
          fill="#8DC925"
        />
      </g>
    </svg>
  )
}

// Claimable spark particles: light motes rising off the art. `x`/`y` place
// the spawn point (% of the art box, biased low), `dx`/`dy` are the drift
// (px, upward with a slight sway), `delay` staggers the births so the
// stream feels continuous rather than pulsed.
const SPARK_PARTICLES = [
  { x: 18, y: 78, dx: 4, dy: -46, size: 4, delay: 0 },
  { x: 34, y: 86, dx: -5, dy: -54, size: 3, delay: 300 },
  { x: 50, y: 80, dx: 3, dy: -60, size: 5, delay: 600 },
  { x: 66, y: 88, dx: -4, dy: -50, size: 3, delay: 900 },
  { x: 82, y: 78, dx: 5, dy: -44, size: 4, delay: 1200 },
  { x: 26, y: 90, dx: -3, dy: -58, size: 3, delay: 1500 },
]

/** The prize-box artwork: spark, reward image, pending spinner, claimed tick. */
function RewardArt(props: { milestone: MilestoneView; claiming: boolean }) {
  return (
    <>
      <Show when={props.milestone.state === 'claimable'}>
        {/* claimable spark: layered yellow glow, rippling out via the
            wrapper (the keyframe owns transform, so it can't sit on the
            translated circles themselves) */}
        <div class="absolute inset-0 animate-glow-pulse">
          <div class="absolute left-1/2 top-1/2 size-[85px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#DFDD73] opacity-30" />
          <div class="absolute left-1/2 top-1/2 size-[49px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#FFFD9E]" />
          <div class="absolute left-1/2 top-1/2 size-[65px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#FFFD9E] opacity-50" />
          {/* highlights: a soft white sheen plus a hot blurred core, so the
              halo reads bright-centred instead of flat yellow */}
          <div class="absolute left-1/2 top-1/2 size-[72px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-60 [background:radial-gradient(closest-side,rgba(255,255,255,0.9)_0%,rgba(255,255,255,0)_70%)]" />
          <div class="absolute left-1/2 top-1/2 size-[30px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white opacity-90 blur-[5px]" />
        </div>
      </Show>
      <img
        src={milestoneRewardImages[props.milestone.index]}
        alt=""
        class="relative size-[60px] select-none object-contain"
        classList={{
          'opacity-60': props.claiming,
          'animate-claim-scale': props.milestone.state === 'claimable',
        }}
      />
      <Show when={props.milestone.state === 'claimable'}>
        {/* spark particles: staggered light motes drifting up from the low
            half of the art (positioned via left/top because the keyframe
            owns transform). Rendered after the art so they float over the
            illustration instead of hiding behind it. */}
        <div class="pointer-events-none absolute inset-0">
          <For each={SPARK_PARTICLES}>
            {(p) => (
              <div
                class="absolute animate-spark-rise rounded-full bg-[#FFFD9E]"
                style={{
                  left: `${p.x}%`,
                  top: `${p.y}%`,
                  width: `${p.size}px`,
                  height: `${p.size}px`,
                  margin: `-${p.size / 2}px`,
                  'animation-delay': `${p.delay}ms`,
                  '--dx': `${p.dx}px`,
                  '--dy': `${p.dy}px`,
                }}
              />
            )}
          </For>
        </div>
      </Show>
      <Show when={props.claiming}>
        <div class="absolute inset-0 flex items-center justify-center">
          <Spinner class="size-6 text-fern" />
        </div>
      </Show>
      <Show when={props.milestone.state === 'claimed'}>
        {/* pop-in on claim; the wrapper owns the centring translate so the
            keyframe's transform doesn't fight it */}
        <div class="absolute left-1/2 top-[calc(50%+4px)] -translate-x-1/2 -translate-y-1/2">
          <ClaimedTick class="animate-tick-pop drop-shadow-[0_0_3.58px_rgba(116,200,171,0.95)]" />
        </div>
      </Show>
    </>
  )
}

function RewardLabel(props: { index: number; class?: string }) {
  const visual = () => rewardVisual(props.index)
  return (
    <Show when={visual()}>
      {(v) => (
        <span
          class={cn(
            'hidden min-w-0 flex-1 origin-left break-normal font-bold leading-tight text-moss [-webkit-text-stroke:4px_white] [paint-order:stroke] min-[350px]:block max-[374px]:scale-90',
            props.class,
          )}
        >
          {v().kind === 'content'
            ? (v() as { name: string }).name
            : String((v() as { amount: number }).amount)}
        </span>
      )}
    </Show>
  )
}

export function ProgressCard(props: {
  milestones: MilestoneView[]
  accumulatedScore: number
  /** `requiredScore` of the milestone whose claim call is in flight. */
  claimingScore: number | undefined
  onClaim: (milestone: MilestoneView) => void
}) {
  const thresholds = () => props.milestones.map((mv) => mv.requiredScore)
  const lastDotY = () => dotY(props.milestones.length - 1)
  const headY = () => markerY(props.accumulatedScore, thresholds())
  const complete = () =>
    props.milestones.length > 0 &&
    props.accumulatedScore >= thresholds()[props.milestones.length - 1]

  return (
    <div class="gradient-outline rounded-[32px] bg-white/[0.53] px-6 pb-[15px] pt-6">
      {/* column headers */}
      <div class="mb-2 flex items-center justify-between gap-7">
        <div class="flex h-12 flex-1 items-center justify-center rounded-2xl bg-[#e3f2ed]/70 px-2.5 text-center text-xs text-fern">
          {m.invitees_focus_time()}
        </div>
        <div class="flex h-12 flex-1 items-center justify-center rounded-2xl bg-[#e3f2ed]/70 px-2.5 text-center text-xs text-fern">
          <span class="whitespace-pre-line">{m.your_rewards()}</span>
        </div>
      </div>

      {/* timeline */}
      <div class="relative">
        {/* white rail (unfilled track) */}
        <div
          class="absolute left-1/2 w-2.5 -translate-x-1/2 rounded-full bg-white"
          style={{
            top: `${RAIL_TOP}px`,
            height: `${lastDotY() + CAP - RAIL_TOP}px`,
          }}
        />
        {/* dark-green fill, its rounded end-cap centred on the head */}
        <div
          class="absolute left-1/2 w-1.5 -translate-x-1/2 rounded-full bg-fern"
          style={{
            top: `${RAIL_TOP}px`,
            height: `${headY() + CAP - RAIL_TOP}px`,
          }}
        />
        {/* sprout leaves cover the rail/fill top */}
        <div class="absolute left-1/2 top-0 -translate-x-1/2">
          <Sprout class="h-[21px] w-[23px]" />
        </div>

        {/* current-progress head: soft halo + glowing dot, pulsing in sync
            (wrappers own the centring translate; the keyframes own
            scale/opacity, same split as the claimable spark) */}
        <div
          class="absolute left-1/2 size-7 -translate-x-1/2 -translate-y-1/2"
          style={{ top: `${headY()}px` }}
        >
          <div class="size-full animate-head-halo rounded-full [background:radial-gradient(closest-side,#44D39E_40.87%,rgba(68,211,158,0)_100%)]" />
        </div>
        <div
          class="absolute left-1/2 size-1 -translate-x-1/2 -translate-y-1/2"
          style={{ top: `${headY()}px` }}
        >
          <div class="size-full animate-head-dot rounded-full bg-[#30FFB2] drop-shadow-[0_0_3.75px_rgba(35,255,173,0.93)]" />
        </div>

        {/* current-progress pill, its arrow tip touching the rail */}
        <Show when={!complete()}>
          <div
            class="absolute left-1/2 z-10 -translate-x-[calc(100%+13px)] -translate-y-1/2"
            style={{ top: `${headY()}px` }}
          >
            <div class="relative flex h-[22px] items-center gap-0.5 rounded-full bg-white px-1 shadow-[0_1px_30px_rgba(0,0,0,0.15)]">
              <span class="text-[10px] leading-[14px] text-black">
                {props.accumulatedScore}
              </span>
              <span class="text-[10px] leading-[14px] text-black">
                {m.minutes_text()}
              </span>
              <svg
                class="absolute left-full top-1/2 -translate-y-1/2"
                width="9"
                height="7"
                viewBox="0 0 9 7"
                fill="none"
                aria-hidden="true"
              >
                <path d="M8.25 3.5L0 7V0L8.25 3.5Z" fill="white" />
              </svg>
            </div>
          </div>
        </Show>

        {/* sapling zone above the first dot */}
        <div style={{ height: `${TOP_PAD}px` }} />

        {/* milestone rows */}
        <For each={props.milestones}>
          {(milestone, index) => (
            <div
              class="flex items-center justify-center gap-2"
              style={{ height: `${ROW_HEIGHT}px` }}
            >
              {/* minutes — min-w-0 keeps both flex-1 halves truly equal (the
                  default min-width:auto lets wide content push the dot off
                  the rail's centre); justify-end anchors the text to the
                  dot side, so any overflow spills left, away from the rail */}
              <div class="flex min-w-0 flex-1 origin-right items-baseline justify-end gap-1 text-fern max-[374px]:scale-90">
                <span class="text-2xl font-bold">
                  {milestone.requiredScore}
                </span>
                <span class="text-sm">{m.minutes_text()}</span>
              </div>

              {/* dot */}
              <div class="relative flex size-[30px] shrink-0 items-center justify-center">
                <Show
                  when={milestone.state !== 'locked'}
                  fallback={<div class="size-[30px] rounded-full bg-white" />}
                >
                  <div class="flex size-[26px] items-center justify-center rounded-full border-[3px] border-fern/60 bg-fern bg-clip-padding">
                    <StepCheck />
                  </div>
                </Show>
              </div>

              {/* reward */}
              <div class="flex min-w-0 flex-1 items-center">
                <Show
                  when={milestone.state === 'claimable'}
                  fallback={
                    <div class="relative ml-[-2px] mr-[-2px] shrink-0">
                      <RewardArt milestone={milestone} claiming={false} />
                    </div>
                  }
                >
                  <button
                    type="button"
                    // Press feedback on the wrapper: the art's own claim
                    // animation owns the img transform, so the squash must
                    // live one level up to compose with it.
                    class="relative ml-[-2px] mr-[-2px] shrink-0 transition-transform duration-150 ease-out active:scale-95"
                    aria-label={m.claim()}
                    onClick={() => props.onClaim(milestone)}
                  >
                    <RewardArt
                      milestone={milestone}
                      claiming={props.claimingScore === milestone.requiredScore}
                    />
                  </button>
                </Show>
                <RewardLabel
                  index={milestone.index}
                  class={
                    index() >= props.milestones.length - 2
                      ? 'text-sm leading-none'
                      : undefined
                  }
                />
              </div>
            </div>
          )}
        </For>
      </div>
    </div>
  )
}
